import React, { useState, useRef, useEffect } from 'react'
import { ChevronDown, CheckCircle } from 'lucide-react'
import { Input } from './Input'

export interface ColorOption {
 value: string
 label: string
 hex?: string
}

interface ColorPickerProps {
 value: string
 options: ColorOption[]
 onChange: (option: ColorOption) => void
 placeholder?: string
 disabled?: boolean
 error?: string
}

export const ColorPicker: React.FC<ColorPickerProps> = ({
 value,
 options,
 onChange,
 placeholder = 'Selecione uma cor',
 disabled = false,
 error
}) => {
 const [search, setSearch] = useState('')
 const [showDropdown, setShowDropdown] = useState(false)
 const dropdownRef = useRef<HTMLDivElement>(null)

 const selectedColor = options.find(c => c.value === value)
 const displayValue = showDropdown ? search : selectedColor?.label || ''

 // Filtra cores baseado na busca
 const filteredColors = options.filter(color =>
 color.label.toLowerCase().includes(search.toLowerCase())
 )

 // Fecha dropdown ao clicar fora
 useEffect(() => {
 const handleClickOutside = (event: MouseEvent) => {
 if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
 setShowDropdown(false)
 setSearch('')
 }
 }

 if (showDropdown) {
 document.addEventListener('mousedown', handleClickOutside)
 }

 return () => {
 document.removeEventListener('mousedown', handleClickOutside)
 }
 }, [showDropdown])

 const handleSelect = (color: ColorOption) => {
 onChange(color)
 setSearch('')
 setShowDropdown(false)
 }

 return (
 <div className="relative" ref={dropdownRef}>
<div className="relative">
        <Input
          type="text"
          value={displayValue}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => setShowDropdown(true)}
          placeholder={placeholder}
          disabled={disabled}
          className={`pr-10 ${error ? 'border-error focus-visible:ring-error focus-visible:border-error' : ''}`}
        />
        {selectedColor?.hex && (
          <div
            className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full border-2 border-border"
            style={{ backgroundColor: selectedColor.hex }}
          />
        )}
        <ChevronDown 
          className={`absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground transition-transform ${
            showDropdown ? 'rotate-180' : ''
          }`}
/>
      </div>
      {error && (
        <p className="mt-1 text-sm text-error">{error}</p>
      )}

      {/* Dropdown */}
 {showDropdown && !disabled && (
 <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto">
 {filteredColors.length === 0 ? (
 <div className="p-4 text-center text-muted-foreground text-sm">
 Nenhuma cor encontrada
 </div>
 ) : (
 <div className="py-1">
 {filteredColors.map((color) => (
 <button
 key={color.value}
 type="button"
 onClick={() => handleSelect(color)}
 className={`w-full text-left px-3 py-2.5 hover:bg-surface flex items-center space-x-3 transition-colors ${
 color.value === value ? 'bg-accent/5 dark:bg-primary-900/20' : ''
 }`}
 >
 {/* Preview da cor */}
 {color.hex && (
 <div
 className="w-6 h-6 rounded-full border-2 border-border flex-shrink-0"
 style={{ backgroundColor: color.hex }}
 />
 )}
 
 {/* Nome da cor */}
 <div className="flex-1 font-medium text-foreground">
 {color.label}
 </div>

 {/* Checkmark */}
 {color.value === value && (
 <CheckCircle className="h-4 w-4 text-accent flex-shrink-0" />
 )}
 </button>
 ))}
 </div>
 )}
 </div>
 )}
 </div>
 )
}

